import React, { useEffect, useState } from 'react';
import { Facebook, Globe, Linkedin, Phone, FileText, Users, Instagram, Mail, Loader2 } from 'lucide-react';
import { getSourceOptions } from '@/actions/fetch-source-options';
import type { SourceOption } from '../types';

interface SourceSelectorProps {
  selectedSource: string;
  onSourceSelect: (sourceUuid: string, sourceName: string) => void;
}

// Icon mapping for different source types
const getIconForSource = (sourceName: string) => {
  const name = sourceName.toLowerCase();
  if (name.includes('facebook') || name.includes('fb')) return <Facebook className="h-8 w-8" />;
  if (name.includes('google') || name.includes('ads')) return <Mail className="h-8 w-8" />;
  if (name.includes('website') || name.includes('web')) return <Globe className="h-8 w-8" />;
  if (name.includes('linkedin')) return <Linkedin className="h-8 w-8" />;
  if (name.includes('instagram') || name.includes('insta')) return <Instagram className="h-8 w-8" />;
  if (name.includes('phone') || name.includes('call')) return <Phone className="h-8 w-8" />;
  if (name.includes('manual') || name.includes('entry')) return <FileText className="h-8 w-8" />;
  return <Users className="h-8 w-8" />;
};

const SourceSelector: React.FC<SourceSelectorProps> = ({ selectedSource, onSourceSelect }) => {
  const [sources, setSources] = useState<SourceOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch source column with options from API
      const response = await getSourceOptions();

      if (response.success && response.data?.columns) {
        // Find the source column
        const sourceColumn = response.data.columns.find(col => col.col_name === 'source');

        if (sourceColumn && sourceColumn.options && sourceColumn.options.length > 0) {
          // Map API options to SourceOption format with icons
          const sourcesWithIcons: SourceOption[] = sourceColumn.options
            .filter(opt => opt.active !== false) // Only show active options
            .map(option => ({
              value: option.uuid,
              label: option.opt_name,
              description: `Import leads from ${option.opt_name}`,
              icon: getIconForSource(option.opt_name)
            }));

          setSources(sourcesWithIcons);
        } else {
          setError('No source options found. Please configure source options in Form Fields settings.');
        }
      } else {
        setError('Failed to load source options from server.');
      }

      setLoading(false);
    } catch (err) {
      setError('Failed to load source options. Please try again.');
      setLoading(false);
      console.error('Error fetching sources:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Select Lead Source
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Choose the source where these leads are coming from. This helps in tracking and analytics.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {sources.map((source) => (
          <button
            key={source.value}
            type="button"
            onClick={() => onSourceSelect(source.value, source.label)}
            className={`
              relative flex flex-col items-center p-6 rounded-lg border-2 transition-all duration-200
              hover:shadow-lg hover:scale-105
              ${
                selectedSource === source.value
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500 ring-offset-2'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300'
              }
            `}
          >
            {/* Icon */}
            <div
              className={`
                mb-3 p-3 rounded-full transition-colors
                ${
                  selectedSource === source.value
                    ? 'bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }
              `}
            >
              {source.icon}
            </div>

            {/* Label */}
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
              {source.label}
            </h4>

            {/* Description */}
            <p className="text-xs text-center text-gray-500 dark:text-gray-400">
              {source.description}
            </p>

            {/* Selected indicator */}
            {selectedSource === source.value && (
              <div className="absolute top-2 right-2">
                <div className="h-5 w-5 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            )}
          </button>
        ))}
      </div>

      {selectedSource && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <span className="font-semibold">Selected:</span>{' '}
            {sources.find((s) => s.value === selectedSource)?.label} - All imported leads will be tagged with this source.
          </p>
        </div>
      )}
    </div>
  );
};

export default SourceSelector;
